import { converter, differenceEuclidean } from 'culori';

export type RgbPixel = [number, number, number];

const toOklch = converter('oklch');
const distance = differenceEuclidean('oklab');

/** Codes for the recoverable failure modes the UI maps to friendly messages. */
export type ExtractErrorCode =
  | 'not-an-image'
  | 'too-large'
  | 'undecodable'
  | 'image-empty-or-transparent';

export class ExtractError extends Error {
  constructor(public readonly code: ExtractErrorCode) {
    super(code);
    this.name = 'ExtractError';
  }
}

export interface ExtractOptions {
  /** Most colors to return across all tiers (see `splitTiers`). */
  colorCount?: number;
  /** Longest edge (px) the image is downscaled to before sampling. */
  maxEdge?: number;
  /** Sample every Nth pixel after downscaling (perf vs. fidelity). */
  sampleStride?: number;
  /** Alpha below this (0–255) is treated as transparent and skipped. */
  alphaThreshold?: number;
  /** Reject files larger than this many bytes. */
  maxBytes?: number;
}

const DEFAULTS = {
  colorCount: 24,
  maxEdge: 1000,
  sampleStride: 4,
  alphaThreshold: 125,
  maxBytes: 25 * 1024 * 1024, // 25 MB
} as const;

/** Format an 8-bit RGB triple as a lowercase #rrggbb string. */
export function rgbToHex([r, g, b]: RgbPixel): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const hex = (n: number) => clamp(n).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/**
 * Flatten an RGBA buffer into opaque RGB pixels, sampling every `stride`th
 * pixel and skipping anything below `alphaThreshold`.
 */
export function samplePixels(
  data: Uint8ClampedArray,
  stride: number = DEFAULTS.sampleStride,
  alphaThreshold: number = DEFAULTS.alphaThreshold,
): RgbPixel[] {
  const step = Math.max(1, Math.floor(stride));
  const pixels: RgbPixel[] = [];
  for (let i = 0; i < data.length; i += 4 * step) {
    if (data[i + 3] < alphaThreshold) continue; // skip ~transparent
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }
  return pixels;
}

/** OKLab distance below which two candidates read as the same swatch. */
const MIN_DISTINCT = 0.09;
/**
 * Bins holding less than this share of sampled pixels are noise (JPEG
 * artefacts, anti-aliasing, a photo's stray shades) and never become swatches.
 * Without it a busy image yields a long tail of one-off colours.
 */
const MIN_SHARE = 0.0005;
/** OKLCH chroma at which a colour counts as fully saturated when ranking. */
const CHROMA_CAP = 0.3;
/** Histogram resolution: 5 bits per channel, 32 levels, 8-wide bins. */
const BITS = 5;
const LEVELS = 1 << BITS;
const SHIFT = 8 - BITS;

/**
 * Pick at most `colorCount` hex colors from sampled pixels, most prominent
 * first. Throws when there is nothing to quantize; may return fewer colors than
 * requested for low-variety images rather than padding.
 *
 * Bins pixels in a coarse RGB histogram and represents each bin by the mean of
 * its own pixels, so every candidate is a colour that is actually in the image.
 * Bins are ranked by share of the image, boosted by chroma so accents beat
 * near-neutral backgrounds, and a candidate too close to one already picked is
 * skipped. (Median cut was tried first: it splits by population and returns box
 * averages, so a big background took most boxes and the rest each spanned
 * several hues, averaging to muddy colours nowhere in the image.)
 */
export function quantizePixels(
  pixels: RgbPixel[],
  colorCount: number = DEFAULTS.colorCount,
): string[] {
  if (pixels.length === 0) throw new ExtractError('image-empty-or-transparent');

  const bins = LEVELS ** 3;
  const count = new Uint32Array(bins);
  const sumR = new Uint32Array(bins);
  const sumG = new Uint32Array(bins);
  const sumB = new Uint32Array(bins);
  for (const [r, g, b] of pixels) {
    const i = ((r >> SHIFT) << (2 * BITS)) | ((g >> SHIFT) << BITS) | (b >> SHIFT);
    count[i]++;
    sumR[i] += r;
    sumG[i] += g;
    sumB[i] += b;
  }

  const ranked: { hex: string; score: number }[] = [];
  for (let i = 0; i < bins; i++) {
    if (count[i] / pixels.length < MIN_SHARE) continue;
    const hex = rgbToHex([sumR[i] / count[i], sumG[i] / count[i], sumB[i] / count[i]]);
    const chroma = Math.min(toOklch(hex)?.c ?? 0, CHROMA_CAP) / CHROMA_CAP;
    ranked.push({ hex, score: (count[i] / pixels.length) * (0.15 + chroma) });
  }
  ranked.sort((a, b) => b.score - a.score);

  const palette: string[] = [];
  for (const { hex } of ranked) {
    if (palette.length >= colorCount) break;
    if (palette.every((picked) => distance(picked, hex) >= MIN_DISTINCT)) palette.push(hex);
  }
  return palette;
}

export interface PaletteTier {
  id: 'primary' | 'secondary' | 'additional';
  label: string;
  colors: string[];
}

/** How many of the ranked colors each tier takes; the rest are additional. */
const TIER_SIZES = { primary: 6, secondary: 6 } as const;

/**
 * Split a ranked palette into the tiers the UI shows: the brand's main colors,
 * a second rank, and everything else distinct enough to keep. Empty tiers are
 * dropped, so a low-variety image just shows one group.
 */
export function splitTiers(colors: string[]): PaletteTier[] {
  const p = TIER_SIZES.primary;
  const s = p + TIER_SIZES.secondary;
  return [
    { id: 'primary' as const, label: 'Primary', colors: colors.slice(0, p) },
    { id: 'secondary' as const, label: 'Secondary', colors: colors.slice(p, s) },
    { id: 'additional' as const, label: 'Additional', colors: colors.slice(s) },
  ].filter((tier) => tier.colors.length > 0);
}

/** Compute downscaled dimensions so the longest edge is <= maxEdge (never upscales). */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

interface ImageSource {
  width: number;
  height: number;
}

/** Draw an image source downscaled onto a canvas and return its RGBA data. */
function getImageData(
  source: CanvasImageSource & ImageSource,
  maxEdge: number,
): Uint8ClampedArray {
  const { width, height } = fitWithin(source.width, source.height, maxEdge);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new ExtractError('undecodable');
  ctx.drawImage(source, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height).data;
}

/**
 * Full pipeline: validate a File → decode → downscale → sample → quantize.
 * Returns dominant colors as #rrggbb strings (most-dominant first).
 */
export async function extractPalette(
  file: File,
  opts: ExtractOptions = {},
): Promise<string[]> {
  const o = { ...DEFAULTS, ...opts };

  if (file.type && !file.type.startsWith('image/')) throw new ExtractError('not-an-image');
  if (file.size > o.maxBytes) throw new ExtractError('too-large');

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file); // first frame for animated GIFs
  } catch {
    throw new ExtractError('undecodable');
  }

  try {
    const data = getImageData(bitmap, o.maxEdge);
    const pixels = samplePixels(data, o.sampleStride, o.alphaThreshold);
    return quantizePixels(pixels, o.colorCount);
  } finally {
    bitmap.close();
  }
}
